export const QUESTION_ORDER = ['relationship', 'interests', 'location', 'occasion'] as const

export type TapSignal = typeof QUESTION_ORDER[number]

// A null value is a display-only option: a custom closed-vocabulary entry no
// list resolves — tapping it renders the buyer's words but sends nothing, the
// same rule the madlibs blanks follow.
export type TapOption = { label: string, value: string | null, display?: string }

// TEMPORARY (concept-proving): hand-picked options per question. Interests and
// cities are a snapshot of the popular-GiftTag / top-featured-city rankings the
// live endpoints (Consumer::InterestsController#popular,
// Consumer::LocationsController#popular) will feed at integration; relationships
// are the canonical list. Every value is a canonical the matcher already knows,
// so a tap can never produce an unsearchable signal; `display` carries the
// buyer-facing wording where it differs ("coworker" is what they tapped,
// `colleague` is what the request sends).
export const TAP_OPTIONS = {
  interests: [
    { label: 'Coffee', value: 'coffee' },
    { label: 'Wine', value: 'wine' },
    { label: 'Sports', value: 'sports' },
    { label: 'Travel', value: 'travel' },
    { label: 'Golf', value: 'golf' },
    { label: 'Movies', value: 'movies' },
    { label: 'Outdoors', value: 'outdoors' },
    { label: 'Music', value: 'music' },
    { label: 'Books', value: 'books' },
    { label: 'Beauty', value: 'beauty' },
    { label: 'Fitness', value: 'fitness' },
    { label: 'Gaming', value: 'gaming' },
  ],
  location: [
    { label: 'New York', value: 'New York, NY', display: 'New York' },
    { label: 'Chicago', value: 'Chicago, IL', display: 'Chicago' },
    { label: 'Los Angeles', value: 'Los Angeles, CA', display: 'Los Angeles' },
    { label: 'San Francisco', value: 'San Francisco, CA', display: 'San Francisco' },
    { label: 'San Diego', value: 'San Diego, CA', display: 'San Diego' },
    { label: 'Boston', value: 'Boston, MA', display: 'Boston' },
    { label: 'Washington DC', value: 'Washington, DC', display: 'Washington DC' },
    { label: 'Denver', value: 'Denver, CO', display: 'Denver' },
    { label: 'Houston', value: 'Houston, TX', display: 'Houston' },
    { label: 'Portland', value: 'Portland, OR', display: 'Portland' },
  ],
} as const satisfies Record<'interests' | 'location', TapOption[]>

// The whole finite relationship vocabulary, common-first — the first
// VISIBLE_RELATIONSHIP_COUNT show up front and the rest sit behind "Someone
// else…". Generic quick picks (Spouse, Kids) lead; their gendered specifics
// (Wife/Husband, Daughter/Son) live below for buyers who want to pin one down.
// TEMPORARY (concept-proving): the ordering is a hand-guess pending purchase
// data; seasonal boosting then pulls the day's relationship to the very front.
export const VISIBLE_RELATIONSHIP_COUNT = 6

export const RELATIONSHIP_OPTIONS = [
  { label: 'Friend', value: 'friend' },
  { label: 'Coworker', value: 'colleague', display: 'coworker' },
  { label: 'Spouse', value: 'spouse', display: 'spouse' },
  { label: 'Kids', value: 'child', display: 'kids' },
  { label: 'Mom', value: 'mom' },
  { label: 'Dad', value: 'dad' },
  { label: 'Sister', value: 'sister' },
  { label: 'Brother', value: 'brother' },
  { label: 'Wife', value: 'spouse', display: 'wife' },
  { label: 'Husband', value: 'spouse', display: 'husband' },
  { label: 'Girlfriend', value: 'girlfriend' },
  { label: 'Boyfriend', value: 'boyfriend' },
  { label: 'Partner', value: 'partner' },
  { label: 'Daughter', value: 'child', display: 'daughter' },
  { label: 'Son', value: 'child', display: 'son' },
  { label: 'Grandma', value: 'grandparent', display: 'grandma' },
  { label: 'Grandpa', value: 'grandparent', display: 'grandpa' },
  { label: 'Best friend', value: 'bestie', display: 'best friend' },
  { label: 'Aunt', value: 'aunt' },
  { label: 'Uncle', value: 'uncle' },
  { label: 'Cousin', value: 'cousin' },
  { label: 'Boss', value: 'boss' },
  { label: 'Neighbor', value: 'neighbor' },
  { label: 'Family', value: 'family' },
  { label: 'Grandchild', value: 'grandchild' },
  { label: 'Crush', value: 'crush' },
  { label: 'Direct report', value: 'direct_report' },
] as const satisfies TapOption[]

export const OPEN_LABELS = {
  relationship: 'Someone else…',
  interests: 'Add your own…',
  location: 'Somewhere else…',
  occasion: 'Something else…',
} as const satisfies Record<TapSignal, string>

export const MORE_LABELS = {
  relationship: 'More people',
  interests: 'More interests',
  location: 'More places',
  occasion: 'More occasions',
} as const satisfies Record<TapSignal, string>

// Interests cap: enough to seed a quick-gift search without turning the step
// into an endless checklist.
export const INTEREST_LIMIT = 2

// Three inline options keep a collapsed row to a single un-wrapped line on the
// 620px column (the row is nowrap); the rest sit behind "more…" and the
// expanded question.
export const INLINE_OPTION_LIMIT = 3

export const QUESTION_HINTS = {
  relationship: 'tap one',
  interests: 'pick up to 2',
  location: 'tap one',
  occasion: 'tap one',
} as const satisfies Record<TapSignal, string>

// She/he follows the tapped word's own gender ("sister" → she); everything else
// — friend, colleague, a custom entry — asks with "they".
export const SUBJECT_PRONOUNS: Record<string, 'she' | 'he'> = {
  mom: 'she',
  sister: 'she',
  aunt: 'she',
  girlfriend: 'she',
  wife: 'she',
  grandma: 'she',
  daughter: 'she',
  dad: 'he',
  brother: 'he',
  uncle: 'he',
  boyfriend: 'he',
  husband: 'he',
  grandpa: 'he',
  son: 'he',
}
